/** @jsxImportSource theme-ui */
import { useEffect, useState } from "react";
import { Box, Text, Button as ThemeUiButton, useThemeUI } from "theme-ui";
import { motion, AnimatePresence } from "framer-motion";
import FocusLock from "react-focus-lock";
import debounce from "lodash/debounce";

import {
  BladeGuide,
  BladePatternGuide,
  FlexGuide,
  ProductLogos,
} from "components";
import { ChevronDownIcon, CloseIcon } from "components/icons";
import * as styles from "./Modal.styles";

export default function Modal({
  isTopMostModal,
  appLogo,
  outerTitle,
  title,
  subTitle,
  onClose = () => {},
  closeButtonLabel,
  content = [],
  children,
  isFullWidth = false,
  buttonLabel: _buttonLabel,
  ...props
}) {
  const {
    theme: { isDesktop },
  } = useThemeUI();

  const [isOpened, setIsOpened] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const close = () => setIsVisible(false);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape" && isTopMostModal) {
        close();
      }
    }

    const handleKeyDownDebounced = debounce(handleKeyDown);

    document.addEventListener("keydown", handleKeyDownDebounced);
    return () => {
      document.removeEventListener("keydown", handleKeyDownDebounced);
    };
  }, [isTopMostModal]);

  return (
    <AnimatePresence>
      {isVisible && (
        <FocusLock returnFocus disabled={!isOpened}>
          <motion.div
            sx={styles.modalContainer}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.3 }}
            onAnimationComplete={(definition) => {
              if (definition.y === 0) {
                setIsOpened(true);
              }
              if (definition.y === "100%") {
                onClose();
              }
            }}
          >
            {isDesktop && (
              <Box sx={styles.logos}>
                <ProductLogos appLogo={appLogo}>
                  {outerTitle && <Text variant="h5Light">{outerTitle}</Text>}
                </ProductLogos>
              </Box>
            )}

            <ThemeUiButton
              variant="unstyled"
              sx={styles.closeButton}
              onClick={close}
              aria-label={closeButtonLabel}
            >
              {!isDesktop && closeButtonLabel}
              {isDesktop ? (
                <CloseIcon strokeWidth="10" stroke="#000000" />
              ) : (
                <ChevronDownIcon />
              )}
            </ThemeUiButton>

            <Box sx={styles.modalContainerScroll}>
              <Box sx={styles.modal({ isFullWidth })} {...props}>
                {title && (
                  <Box sx={styles.modalTitle}>
                    <Text variant="h3Bold">{title}</Text>
                  </Box>
                )}

                {subTitle && (
                  <Box sx={styles.modalSubtitle}>
                    <Text as="div" variant="h5Light">
                      {subTitle}
                    </Text>
                  </Box>
                )}

                {content.length > 0 && (
                  <Box sx={styles.modalContent}>
                    {content
                      .map(
                        (
                          {
                            component = "Text",
                            componentProps = {},
                            children: componentChildren,
                          },
                          index
                        ) => {
                          if (component === "FlexGuide") {
                            return (
                              <FlexGuide key={index} {...componentProps} />
                            );
                          }

                          if (component === "BladePatternGuide") {
                            return (
                              <BladePatternGuide
                                key={index}
                                {...componentProps}
                              />
                            );
                          }

                          if (component === "BladeGuide") {
                            return (
                              <BladeGuide key={index} {...componentProps} />
                            );
                          }

                          if (component === "Text") {
                            return (
                              <Text
                                key={index}
                                as="div"
                                variant="copy3Light"
                                dangerouslySetInnerHTML={{
                                  __html: componentChildren,
                                }}
                                sx={styles.contentText}
                                {...componentProps}
                              />
                            );
                          }
                          return null;
                        }
                      )
                      .filter(Boolean)}
                  </Box>
                )}

                {typeof children === "function"
                  ? children({ close })
                  : children && <Box sx={styles.modalContent}>{children}</Box>}
              </Box>
            </Box>
          </motion.div>
        </FocusLock>
      )}
    </AnimatePresence>
  );
}
